<script setup lang="ts">
import { defineAsyncComponent, onBeforeMount, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Gift, Route, User } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import Tools from '@/utils/tools'
import Footer from '@/components/Footer.vue'
import AnimatedBackground from '@/components/utils/AnimatedBackground.vue'
import { useNotificationStore } from '@/stores/notificationStore'
import { ENotificationType } from '@/types'

const props = defineProps<Props>()

interface Props {
	mode: 'signin' | 'signup'
}

const OAuth = defineAsyncComponent(() => import('@/components/auth/OAuth.vue'))
const SignIn = defineAsyncComponent(() => import('@/components/user/signup/SignIn.vue'))
const SignUp = defineAsyncComponent(() => import('@/components/user/signup/SignUp.vue'))
const FooterLogin = defineAsyncComponent(() => import('@/components/user/signup/Footer.vue'))

const { t } = useI18n()

const authStore = useAuthStore()
const mode = ref<'signin' | 'signup'>(props?.mode ? props.mode : 'signin')

const referral = shallowRef<boolean>(Tools.getCookie('referral_code') != null)

watch(() => props.mode, () => {
	mode.value = props.mode ?? 'signin'
})

onBeforeMount(() => {
	if (authStore.isAuth()) {
		window.location.href = '/explore'
		setTimeout(() => {
			window.location.reload()
		}, 200)
	}
})

const route = useRoute()
watch(() => route.query, () => {
	if (route?.query?.rel) {
		useNotificationStore().push(ENotificationType.App, {
			key: `notification.create_redirect_signup`,
			delay: 6000,
		})
	}
}, { immediate: true })
</script>

<template>
	<div class="menu:min-h-[calc(100dvh-100px)] px-4 min-h-[calc(100dvh-140px)] menu:mt-[120px] mt-[150px] w-screen">
		<AnimatedBackground />
		<div
			class="modal"
		>
			<h1 class="pt-5 mb-6 flex-center flex-col gap-1">
				<User class="lucide-button-icon-xl bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-1" />

				<template v-if="mode === 'signin'">
					{{ t('login.submit') }}
				</template>
				<template v-else>
					{{ t('register.submit') }}
				</template>
			</h1>
			<div class="absolute -top-10 -right-10 w-40 h-40 bg-indigo-500/15 rounded-full blur-xl" />
			<div class="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-500/15 rounded-full blur-xl" />

			<div class="flex flex-col gap-3 w-full px-2">
				<span
					v-if="mode === 'signup' && referral"
					class="flex-center gap-1 whitespace-nowrap text-sm w-8/12 mx-auto p-2 bg-yellow-400 text-black rounded button-shadow"
				>
					<div class="flex-center">
						<Gift class="lucide-button-icon" />
					</div>
					<span>
						{{ t('register.referral') }}
					</span>
				</span>
				<OAuth />
				<span class="text-sm text-slate-300 w-full text-center">
					{{ t('common.or') }}
				</span>
				<SignIn v-show="mode === 'signin'" />
				<SignUp v-show="mode === 'signup'" />
				<FooterLogin	v-model="mode" />
			</div>
		</div>
	</div>
	<Footer />
</template>

<style scoped lang="scss">
.modal {
	background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
	@apply shadow-2xl shadow-slate-700/80 mx-auto flex items-center justify-center relative overflow-hidden flex-col max-w-[450px] rounded-xl pb-14 px-6;
}
</style>
